import { type ReactNode, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, AlertTriangle, BadgeCheck, Check, Copy, Eye, EyeOff,
  KeyRound, Lock, RefreshCw, Server, ShieldCheck, Smartphone, User,
} from 'lucide-react';
import { Badge, Button, Card, CardHeader, CardTitle, PageHeader } from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import { useAuth } from '@/lib/auth/useAuth';
import { hasAnyRole } from '@/lib/auth/permissions';
import {
  fetchMyClient,
  fetchMyClientSecret,
  generateMyApiKey,
  rotateMyApiKey,
  rotateMySdkToken,
  type GenerateApiKeyResult,
} from '@/features/clients/api';
import keycloak from '@/lib/auth/keycloak';
import { cn } from '@/lib/utils/cn';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_TONE = {
  ACTIVE:     'success',
  ONBOARDING: 'primary',
  INACTIVE:   'warning',
  SUSPENDED:  'danger',
  CANCELLED:  'neutral',
  REVOKED:    'danger',
} as const;

const ROLE_LABELS: Record<string, string> = {
  AUTHMATECH_CLIENT_ADMIN:    'Client Admin',
  AUTHMATECH_CLIENT_OPERATOR: 'Client Operator',
  AUTHMATECH_CLIENT_FINANCE:  'Client Finance',
  AUTHMATECH_CLIENT_VIEWER:   'Client Viewer',
  AUTHMATECH_SUPER_ADMIN:     'Super Admin',
  AUTHMATECH_ADMIN:           'Admin',
  AUTHMATECH_OPERATOR:        'Operator',
  AUTHMATECH_FINANCE:         'Finance',
  AUTHMATECH_SUPPORT:         'Support',
  AUTHMATECH_VIEWER:          'Viewer',
};

// ── Copy Button ───────────────────────────────────────────────────────────────

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${label ?? 'value'}`}
      className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-fg-muted transition-colors hover:bg-surface-subtle hover:text-fg"
    >
      {copied ? (
        <><Check className="h-3 w-3 text-emerald-500" aria-hidden /><span className="text-emerald-600 dark:text-emerald-400">Copied</span></>
      ) : (
        <><Copy className="h-3 w-3" aria-hidden /><span>{label ? `Copy ${label}` : 'Copy'}</span></>
      )}
    </button>
  );
}

// ── Revealed credential banner (shown once after generate / rotate) ───────────

function RevealedBanner({
  label,
  value,
  onDismiss,
  sensitive = false,
}: {
  label: string;
  value: string;
  onDismiss: () => void;
  sensitive?: boolean;
}) {
  const [visible, setVisible] = useState(!sensitive);

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-50 p-4 dark:bg-amber-500/10">
      <div className="mb-1 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
          {label} — copy it now. It will not be shown again.
        </p>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 break-all rounded-lg bg-white/70 px-3 py-2 font-mono text-xs text-amber-900 dark:bg-black/20 dark:text-amber-200">
          {visible ? value : '••••••••••••••••••••••••••••••••'}
        </code>
        {sensitive && (
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            className="shrink-0 rounded-md p-1.5 text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-500/20"
            title={visible ? 'Hide' : 'Reveal'}
          >
            {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        <CopyButton value={value} />
      </div>
      <button
        type="button"
        className="mt-2 text-xs text-amber-700 underline dark:text-amber-400"
        onClick={onDismiss}
      >
        I've copied it — dismiss
      </button>
    </div>
  );
}

// ── Rotation modal ────────────────────────────────────────────────────────────

function RotateModal({
  title,
  description,
  onConfirm,
  onCancel,
  isPending,
  error,
  prefillSecret,
}: {
  title: string;
  description: string;
  onConfirm: (secret: string) => void;
  onCancel: () => void;
  isPending: boolean;
  error: string | null;
  prefillSecret?: string;
}) {
  const [secret, setSecret] = useState(prefillSecret ?? '');
  const [acknowledged, setAcknowledged] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-xl">
        <div className="mb-1 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
            <RefreshCw className="h-4 w-4" aria-hidden />
          </div>
          <h2 className="text-base font-semibold text-fg">{title}</h2>
        </div>
        <p className="mb-5 text-sm text-fg-muted">{description}</p>

        <div className="space-y-4">
          {/* Client secret input */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-fg-muted">
              Client Secret
            </label>
            <div className="relative">
              <input
                type={showSecret ? 'text' : 'password'}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Paste your client secret"
                className="w-full rounded-xl border border-border bg-surface-subtle px-3 py-2.5 pr-10 font-mono text-sm text-fg placeholder:text-fg-subtle focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg"
                tabIndex={-1}
              >
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Acknowledgement */}
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3 hover:bg-surface-subtle">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 accent-primary-600"
            />
            <span className="text-xs text-fg-muted">
              I understand the current credential will be <strong className="text-fg">immediately invalidated</strong> and the new value must be copied and stored before dismissing.
            </span>
          </label>

          {error && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-400">
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!secret.trim() || !acknowledged || isPending}
            onClick={() => onConfirm(secret.trim())}
            className="bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-500"
          >
            {isPending ? (
              <><RefreshCw className="h-3.5 w-3.5 animate-spin" />Rotating…</>
            ) : (
              'Rotate'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Credentials section ───────────────────────────────────────────────────────

type RotateTarget = 'apiKey' | 'sdkToken' | null;

interface RevealedCreds {
  apiKey?: string;
  sdkToken?: string;
  clientSecret?: string;
}

function CredentialsSection({ clientId, clientStatus }: { clientId: string; clientStatus: string }) {
  const qc = useQueryClient();
  const isOnboarding = clientStatus === 'ONBOARDING';

  const [rotateTarget, setRotateTarget] = useState<RotateTarget>(null);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<RevealedCreds>({});
  const [genError, setGenError] = useState<string | null>(null);
  const [secretVisible, setSecretVisible] = useState(false);

  const secretQuery = useQuery({
    queryKey: ['dashboard', 'me', 'client-secret'],
    queryFn: fetchMyClientSecret,
    enabled: !isOnboarding,
    staleTime: 5 * 60 * 1000,
  });
  const storedSecret = secretQuery.data?.clientSecret;

  const generateMutation = useMutation({
    mutationFn: generateMyApiKey,
    onSuccess: (result: GenerateApiKeyResult) => {
      setRevealed({
        apiKey: result.rawApiKey,
        sdkToken: result.rawSdkToken,
        clientSecret: result.clientSecret,
      });
      setGenError(null);
      // Refresh client status (ONBOARDING → ACTIVE) and prefetch the secret
      qc.invalidateQueries({ queryKey: ['dashboard', 'me', 'client'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'me', 'client-secret'] });
    },
    onError: (err) => {
      setGenError(isApiError(err) ? err.firstErrorMessage : 'Credential generation failed.');
    },
  });

  const rotateApiKeyMutation = useMutation({
    mutationFn: (secret: string) => rotateMyApiKey(secret),
    onSuccess: (result) => {
      setRevealed((prev) => ({ ...prev, apiKey: result.newApiKey }));
      setRotateTarget(null);
      setRotateError(null);
    },
    onError: (err) => {
      setRotateError(isApiError(err) ? err.firstErrorMessage : 'Rotation failed. Check your client secret.');
    },
  });

  const rotateSdkMutation = useMutation({
    mutationFn: (secret: string) => rotateMySdkToken(secret),
    onSuccess: (result) => {
      setRevealed((prev) => ({ ...prev, sdkToken: result.sdkToken }));
      setRotateTarget(null);
      setRotateError(null);
    },
    onError: (err) => {
      setRotateError(isApiError(err) ? err.firstErrorMessage : 'Rotation failed. Check your client secret.');
    },
  });

  function handleRotateConfirm(secret: string) {
    setRotateError(null);
    if (rotateTarget === 'apiKey') rotateApiKeyMutation.mutate(secret);
    else if (rotateTarget === 'sdkToken') rotateSdkMutation.mutate(secret);
  }

  const isPending = rotateApiKeyMutation.isPending || rotateSdkMutation.isPending;

  return (
    <>
      {/* Security notice */}
      <Card accent="primary" elevated>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400">
              <KeyRound className="h-4 w-4" aria-hidden />
            </div>
            <CardTitle>API Credentials</CardTitle>
          </div>
        </CardHeader>

        <div className="mb-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/10">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            Keep these credentials secret. The <strong>Server API Key</strong> must only be used server-side — never in mobile apps or frontend code. The <strong>Mobile SDK Token</strong> is limited to SDK session registration and cannot call verification APIs.
          </p>
        </div>

        {/* Customer ID — always visible */}
        <div className="mb-4 rounded-xl border border-border bg-surface-subtle p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Customer ID</p>
              <p className="mt-1 break-all font-mono text-[13px] font-medium text-fg">{clientId}</p>
              <p className="mt-1 text-[11px] text-fg-subtle">
                Use in the <code className="rounded bg-surface px-1 font-mono">X-CLIENT-ID</code> header on all API requests.
              </p>
            </div>
            <CopyButton value={clientId} label="ID" />
          </div>
        </div>

        {/* First-time generation — ONBOARDING only */}
        {isOnboarding && (
          <>
            {genError && (
              <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-500/25 dark:bg-rose-500/10 dark:text-rose-400">
                {genError}
              </p>
            )}
            <div className="rounded-xl border border-dashed border-border p-4 text-center">
              <p className="mb-1 text-sm font-medium text-fg">Account not yet activated</p>
              <p className="mb-3 text-xs text-fg-muted">
                Generate your <strong>Server API Key</strong>, <strong>Mobile SDK Token</strong>, and <strong>Client Secret</strong> in one step. Each value is shown once — copy them all before dismissing.
              </p>
              <Button
                size="sm"
                onClick={() => generateMutation.mutate()}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? (
                  <><RefreshCw className="h-3.5 w-3.5 animate-spin" />Generating…</>
                ) : (
                  'Activate & Generate Credentials'
                )}
              </Button>
            </div>
          </>
        )}

        {/* Revealed banners (show once after generate / rotate) */}
        {revealed.apiKey && (
          <div className="mb-4">
            <RevealedBanner
              label="New Server API Key"
              value={revealed.apiKey}
              onDismiss={() => setRevealed((p) => ({ ...p, apiKey: undefined }))}
            />
          </div>
        )}
        {revealed.sdkToken && (
          <div className="mb-4">
            <RevealedBanner
              label="New Mobile SDK Token"
              value={revealed.sdkToken}
              onDismiss={() => setRevealed((p) => ({ ...p, sdkToken: undefined }))}
            />
          </div>
        )}
        {revealed.clientSecret && (
          <div className="mb-4">
            <RevealedBanner
              label="Client Secret"
              value={revealed.clientSecret}
              sensitive
              onDismiss={() => setRevealed((p) => ({ ...p, clientSecret: undefined }))}
            />
          </div>
        )}
      </Card>

      {/* Three credential cards — visible only after activation */}
      {!isOnboarding && <div className="grid gap-4 lg:grid-cols-3">

        {/* 1 — Server API Key */}
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400">
              <Server className="h-4 w-4" aria-hidden />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-fg">Server API Key</p>
              <span className="inline-flex items-center rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                Backend only
              </span>
            </div>
          </div>
          <p className="mb-4 text-xs text-fg-muted">
            Used for server-to-server Authmatech APIs. Send as <code className="rounded bg-surface-subtle px-1 font-mono">X-API-KEY</code>. Never use in mobile apps or browser code.
          </p>
          <div className="mt-auto flex flex-col gap-2">
            <div className="rounded-xl border border-border bg-surface-subtle px-3 py-2.5">
              <p className="font-mono text-[11px] tracking-widest text-fg-muted">
                ••••••••••••••••
              </p>
              <p className="mt-0.5 text-[10px] text-fg-subtle">Issued at activation — not retrievable here</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={() => { setRotateError(null); setRotateTarget('apiKey'); }}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Rotate API Key
            </Button>
          </div>
        </Card>

        {/* 2 — Mobile SDK Token */}
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400">
              <Smartphone className="h-4 w-4" aria-hidden />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-fg">Mobile SDK Token</p>
              <span className="inline-flex items-center rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700 dark:bg-primary-500/15 dark:text-primary-300">
                Mobile SDK only
              </span>
            </div>
          </div>
          <p className="mb-4 text-xs text-fg-muted">
            Used only inside Authmatech iOS, Android, Flutter, and React Native SDKs for SDK session registration. Cannot call <code className="rounded bg-surface-subtle px-1 font-mono">/v1/api/verify</code>.
          </p>
          <div className="mt-auto flex flex-col gap-2">
            <div className="rounded-xl border border-border bg-surface-subtle px-3 py-2.5">
              <p className="font-mono text-[11px] tracking-widest text-fg-muted">
                ••••••••••••••••
              </p>
              <p className="mt-0.5 text-[10px] text-fg-subtle">Issued at activation — not retrievable here</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={() => { setRotateError(null); setRotateTarget('sdkToken'); }}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Rotate SDK Token
            </Button>
          </div>
        </Card>

        {/* 3 — Client Secret */}
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
              <Lock className="h-4 w-4" aria-hidden />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-fg">Client Secret</p>
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-400">
                Sensitive
              </span>
            </div>
          </div>
          <p className="mb-4 text-xs text-fg-muted">
            Used to authorize credential rotation actions. Required when rotating the Server API Key or Mobile SDK Token.
          </p>
          <div className="mt-auto flex flex-col gap-2">
            {/* Secret value with eye toggle */}
            <div className="rounded-xl border border-border bg-surface-subtle px-3 py-2.5">
              {secretQuery.isLoading ? (
                <div className="h-4 w-full animate-pulse rounded bg-border" />
              ) : storedSecret ? (
                <div className="flex items-center gap-2">
                  <p className={cn('flex-1 break-all font-mono text-[11px] text-fg', !secretVisible && 'tracking-widest text-fg-muted')}>
                    {secretVisible ? storedSecret : '••••••••••••••••'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setSecretVisible((v) => !v)}
                    className="shrink-0 text-fg-muted transition-colors hover:text-fg"
                    title={secretVisible ? 'Hide' : 'Reveal'}
                  >
                    {secretVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                  <CopyButton value={storedSecret} label="Secret" />
                </div>
              ) : (
                <p className="text-[10px] text-fg-subtle">
                  {secretQuery.isError ? 'Failed to load — refresh to retry.' : 'Not available.'}
                </p>
              )}
            </div>
            <p className="text-[10px] text-fg-subtle">
              Keep it safe — it cannot be reset without contacting support.
            </p>
          </div>
        </Card>

      </div>}

      {/* Rotation modal */}
      {rotateTarget && (
        <RotateModal
          title={rotateTarget === 'apiKey' ? 'Rotate Server API Key' : 'Rotate Mobile SDK Token'}
          description={
            rotateTarget === 'apiKey'
              ? 'The current server API key will be immediately invalidated. Update your backend before confirming.'
              : 'The current mobile SDK token will be immediately invalidated. Update your SDK configuration before confirming.'
          }
          onConfirm={handleRotateConfirm}
          onCancel={() => { setRotateTarget(null); setRotateError(null); }}
          isPending={isPending}
          error={rotateError}
          prefillSecret={storedSecret}
        />
      )}
    </>
  );
}

// ── Row helper ────────────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/50 py-2 last:border-0">
      <dt className="shrink-0 text-sm text-fg-muted">{label}</dt>
      <dd className="text-right text-sm font-medium text-fg">{value}</dd>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MyAccountPage() {
  const { user } = useAuth();
  const isClientAdmin = hasAnyRole(user?.roles ?? [], ['AUTHMATECH_CLIENT_ADMIN']);

  const query = useQuery({
    queryKey: ['dashboard', 'me', 'client'],
    queryFn: fetchMyClient,
  });

  const client = query.data;
  const expiryDate = client?.balanceExpiryDate ? new Date(client.balanceExpiryDate) : null;
  const isExpired = expiryDate != null && expiryDate < new Date();
  const isLowBalance = client != null && client.balance > 0 && client.balance <= 500;

  function handleChangePassword() {
    const accountUrl = keycloak.createAccountUrl();
    window.open(accountUrl, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="My Account"
        subtitle="Your identity, profile, and API credentials."
      />

      {query.isLoading && (
        <div className="grid gap-6 lg:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-2xl border border-border bg-surface" />
          ))}
        </div>
      )}

      {query.isError && !query.isLoading && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 dark:border-rose-500/25 dark:bg-rose-500/10"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" aria-hidden />
          <div>
            <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">
              {(() => {
                const msg = isApiError(query.error) ? query.error.firstErrorMessage : 'Could not load account information.';
                return msg.includes('mapping') || msg.includes('authmatech_client_id')
                  ? 'Your account is not linked to a client yet.'
                  : msg;
              })()}
            </p>
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">
              Contact Authmatech support to complete your account setup.
            </p>
          </div>
        </div>
      )}

      {client && (
        <>
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Identity card */}
            <Card elevated>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400">
                    <User className="h-4 w-4" aria-hidden />
                  </div>
                  <CardTitle>Identity</CardTitle>
                </div>
              </CardHeader>
              <div className="mb-5 flex items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xl font-bold text-white ring-4 ring-primary-500/20">
                  {(user?.name ?? user?.preferredUsername ?? '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-base font-semibold text-fg">{user?.name ?? '—'}</p>
                  <p className="text-sm text-fg-muted">{user?.email ?? '—'}</p>
                  {user?.roles && user.roles.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {user.roles
                        .filter((r) => r.startsWith('AUTHMATECH_'))
                        .map((role) => (
                          <span
                            key={role}
                            className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700 dark:bg-primary-500/15 dark:text-primary-300"
                          >
                            <BadgeCheck className="h-2.5 w-2.5" aria-hidden />
                            {ROLE_LABELS[role] ?? role}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </div>
              <dl className="space-y-0">
                <Row label="Username" value={user?.preferredUsername ?? '—'} />
                {user?.clientId && (
                  <Row
                    label="Customer ID"
                    value={<span className="break-all font-mono text-xs text-fg-muted">{user.clientId}</span>}
                  />
                )}
                <Row
                  label="Identity provider"
                  value={
                    <span className="inline-flex items-center gap-1 text-xs font-normal text-fg-muted">
                      <ShieldCheck className="h-3 w-3 text-emerald-500" aria-hidden />
                      Keycloak SSO
                    </span>
                  }
                />
              </dl>
              <div className="mt-5 border-t border-border pt-4">
                <button
                  type="button"
                  onClick={handleChangePassword}
                  className="flex items-center gap-2 rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-fg transition-all hover:border-border-strong hover:bg-surface-subtle interactive-card"
                >
                  <KeyRound className="h-4 w-4 text-fg-muted" aria-hidden />
                  Change Password
                </button>
                <p className="mt-2 text-[11px] text-fg-subtle">
                  Password changes are handled securely by Authmatech Identity.
                </p>
              </div>
            </Card>

            {/* Account details card */}
            <Card elevated>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
                    <BadgeCheck className="h-4 w-4" aria-hidden />
                  </div>
                  <CardTitle>Account Details</CardTitle>
                </div>
              </CardHeader>
              <dl className="space-y-0">
                <Row label="Company" value={client.clientName} />
                {client.contactPersonName && <Row label="Contact person" value={client.contactPersonName} />}
                {client.contactPersonEmail && (
                  <Row
                    label="Contact email"
                    value={
                      <a href={`mailto:${client.contactPersonEmail}`} className="text-primary-600 hover:underline dark:text-primary-400">
                        {client.contactPersonEmail}
                      </a>
                    }
                  />
                )}
                <Row
                  label="Status"
                  value={
                    <Badge tone={STATUS_TONE[client.clientStatus as keyof typeof STATUS_TONE] ?? 'neutral'}>
                      {client.clientStatus}
                    </Badge>
                  }
                />
                <Row
                  label="Remaining credits"
                  value={
                    <span className={cn(isLowBalance && 'text-amber-600 dark:text-amber-400')}>
                      {client.balance.toLocaleString()}
                      {isLowBalance && <span className="ml-1.5 text-xs font-normal text-amber-500">(Low)</span>}
                    </span>
                  }
                />
                <Row
                  label="Package expiry"
                  value={
                    expiryDate ? (
                      <span className={isExpired ? 'text-rose-600 dark:text-rose-400' : ''}>
                        {expiryDate.toLocaleDateString()}
                        {isExpired && ' · Expired'}
                      </span>
                    ) : '—'
                  }
                />
                {client.daysUntilBalanceExpiry > 0 && (
                  <Row
                    label="Days remaining"
                    value={
                      <span className={client.daysUntilBalanceExpiry <= 14 ? 'text-amber-600 dark:text-amber-400' : ''}>
                        {client.daysUntilBalanceExpiry} days
                      </span>
                    }
                  />
                )}
              </dl>
              <p className="mt-4 rounded-xl bg-surface-subtle px-3 py-2.5 text-xs text-fg-muted">
                To extend your package or add credits, contact your Authmatech account manager.
              </p>
            </Card>
          </div>

          {/* Credentials — CLIENT_ADMIN only */}
          {isClientAdmin && user?.clientId && (
            <CredentialsSection clientId={user.clientId} clientStatus={client.clientStatus} />
          )}
        </>
      )}
    </div>
  );
}
